import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

/**
 * MultiSelectAutocomplete — filter component with:
 * - Text search (autocomplete)
 * - Multiple selection with checkboxes
 * - Selected badges with remove-on-click
 * - Styled to match the app dark theme (Valoriza tokens)
 *
 * Props:
 *   options:    string[]         — all available values
 *   value:      string[]         — currently selected values
 *   onChange:   (v: string[])=>void
 *   placeholder?: string
 *   label?:     string
 *   maxHeight?: number (default 260)
 *   testId?:    string
 */
export function MultiSelectAutocomplete({
  options = [],
  value = [],
  onChange,
  placeholder = 'Buscar…',
  label,
  maxHeight = 260,
  testId = 'multiselect',
  disabled = false,
}) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');

  const normalize = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const q = normalize(query);
  const filtered = q
    ? options.filter(o => normalize(o).includes(q))
    : options;

  const toggle = (opt) => {
    const next = value.includes(opt)
      ? value.filter(v => v !== opt)
      : [...value, opt];
    onChange(next);
  };
  const clearAll = () => onChange([]);

  const selectedCount = value.length;
  const triggerLabel = selectedCount === 0
    ? (label || 'Seleccionar…')
    : selectedCount === 1
      ? value[0]
      : `${selectedCount} seleccionados`;

  // --- styles ---
  const styles = {
    trigger: {
      minWidth: 200, maxWidth: 340,
      padding: '8px 12px',
      background: '#1e293b',
      border: '1px solid #334155',
      color: '#f1f5f9',
      borderRadius: 6,
      fontSize: 13,
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', gap: 8,
      justifyContent: 'space-between',
      opacity: disabled ? 0.5 : 1,
    },
    badge: {
      padding: '1px 8px', borderRadius: 10,
      background: '#60a5fa', color: '#0f172a',
      fontSize: 11, fontWeight: 600,
    },
    popover: {
      width: 340,
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      overflow: 'hidden',
      zIndex: 1000,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      background: '#0f172a',
      border: 'none',
      borderBottom: '1px solid #334155',
      color: '#f1f5f9',
      fontSize: 13,
      outline: 'none',
    },
    optionRow: (selected) => ({
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: 13,
      color: '#f1f5f9',
      display: 'flex', alignItems: 'center', gap: 8,
      background: selected ? 'rgba(96, 165, 250, 0.08)' : 'transparent',
    }),
    checkbox: (checked) => ({
      width: 14, height: 14, borderRadius: 3,
      border: `1.5px solid ${checked ? '#60a5fa' : '#64748b'}`,
      background: checked ? '#60a5fa' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }),
    footer: {
      padding: '8px 12px',
      borderTop: '1px solid #334155',
      display: 'flex', justifyContent: 'space-between',
      background: '#273449',
      fontSize: 12, color: '#94a3b8',
    },
    footerBtn: {
      background: 'transparent', border: 'none', color: '#60a5fa',
      cursor: 'pointer', fontSize: 12, padding: '2px 6px',
    },
  };

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button style={styles.trigger} data-testid={`${testId}-trigger`} disabled={disabled}>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: selectedCount === 0 ? '#94a3b8' : '#f1f5f9',
          }}>
            {label && <span style={{ color: '#94a3b8', marginRight: 6 }}>{label}:</span>}
            {triggerLabel}
          </span>
          {selectedCount > 0 && <span style={styles.badge}>{selectedCount}</span>}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} style={styles.popover} data-testid={`${testId}-popover`}>
          <input
            type="text"
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder}
            style={styles.input}
            data-testid={`${testId}-search`}
          />
          <div style={{ maxHeight, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                Sin resultados
              </div>
            ) : (
              filtered.map(opt => {
                const selected = value.includes(opt);
                return (
                  <div
                    key={opt}
                    style={styles.optionRow(selected)}
                    onClick={() => toggle(opt)}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(96, 165, 250, 0.16)'}
                    onMouseLeave={e => e.currentTarget.style.background = selected ? 'rgba(96, 165, 250, 0.08)' : 'transparent'}
                    data-testid={`${testId}-option-${opt}`}
                  >
                    <span style={styles.checkbox(selected)}>
                      {selected && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="4">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </span>
                    <span style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{opt}</span>
                  </div>
                );
              })
            )}
          </div>
          <div style={styles.footer}>
            <span>{selectedCount} de {options.length}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedCount > 0 && (
                <button onClick={clearAll} style={styles.footerBtn} data-testid={`${testId}-clear`}>
                  Limpiar
                </button>
              )}
              <button onClick={() => setOpen(false)} style={styles.footerBtn}>Cerrar</button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default MultiSelectAutocomplete;
